import { createElement, Fragment } from 'react'

import type { ReactNode } from 'react'

/**
 * Безпечний показ HTML повідомлення чату — без `dangerouslySetInnerHTML`.
 *
 * Бек зберігає `content` як є (лише `strip_tags` для пошуку й перевірки на
 * порожнечу), тож тіло, надіслане напряму в API, може містити що завгодно:
 * `<img src=x onerror=…>` через `innerHTML` виконався б у браузері кожного
 * учасника чату (збережений XSS). Тому HTML розбираємо `DOMParser`-ом — його
 * документ інертний: скрипти не виконуються, зображення не вантажаться, — і
 * будуємо React-вузли лише з білого списку. Текст React екранує сам.
 *
 * Білий список — те, що дає редактор чату (`TiptapEditor`: StarterKit без
 * заголовків, списків і цитат + посилання й згадки), плюс списки й цитата
 * для старих повідомлень. Невідомий тег розгортається (лишається його
 * текст), а небезпечний разом із вмістом (`script`, `style`, …) — зникає.
 * Атрибутів із тексту не беремо жодних, крім `href` посилання (лише
 * http, https, mailto) і даних згадки.
 *
 * Бек тепер санітизує текст при збереженні (`ChatMessageSanitizer`), але
 * повідомлення, записані до того, лежать у базі сирими — тож білий список
 * тримаємо і тут.
 */

/** Тег → що малюємо. Решта тегів розгортається до вмісту. */
const ALLOWED_TAGS = new Set([
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'strike',
    'del',
    'code',
    'pre',
    'a',
    'span',
    'ul',
    'ol',
    'li',
    'blockquote',
])

/** Теги, які прибираємо разом із вмістом: їхній текст — не текст повідомлення. */
const DROPPED_TAGS = new Set([
    'script',
    'style',
    'iframe',
    'frame',
    'object',
    'embed',
    'template',
    'noscript',
    'svg',
    'math',
    'textarea',
    'select',
    'title',
    'head',
])

/** Схеми посилань — ті самі, що пропускає бек (`ChatMessageSanitizer`). */
const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:'])

/** Глибше за це вкладення не розбираємо — далі лише текст. */
const MAX_DEPTH = 64

/**
 * Адреса посилання, якщо вона безпечна, інакше `null`.
 *
 * Схему визначає парсер `URL` так само, як браузер: пробіли й керувальні
 * символи всередині (`java\tscript:`) він відкидає, тож обійти перевірку
 * ними не вийде. Відносних адрес редактор не створює — їх не пропускаємо.
 */
export function safeHref(raw: string | null | undefined): string | null {
    if (!raw) return null

    try {
        const url = new URL(raw.trim())

        return SAFE_PROTOCOLS.has(url.protocol) ? url.href : null
    } catch {
        return null
    }
}

const convertChildren = (node: Node, depth: number): ReactNode[] =>
    Array.from(node.childNodes).map((child, index) => <Fragment key={index}>{convertNode(child, depth + 1)}</Fragment>)

function convertNode(node: Node, depth: number): ReactNode {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? ''

    if (node.nodeType !== Node.ELEMENT_NODE) return null

    const element = node as Element
    const tag = element.tagName.toLowerCase()

    if (DROPPED_TAGS.has(tag)) return null

    if (depth > MAX_DEPTH) return element.textContent ?? ''

    const children = convertChildren(element, depth)

    if (!ALLOWED_TAGS.has(tag)) return <>{children}</>

    if (tag === 'br') return <br />

    if (tag === 'a') {
        const href = safeHref(element.getAttribute('href'))

        // Небезпечна адреса — лишаємо текст посилання без посилання
        if (href === null) return <>{children}</>

        return (
            <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
            </a>
        )
    }

    if (tag === 'span') {
        // Згадка tiptap: `<span class="mention" data-type="mention" data-id="…">@Ім'я</span>`
        if (!element.classList.contains('mention')) return <>{children}</>

        const id = element.getAttribute('data-id')

        return (
            <span className="mention" data-type="mention" {...(id ? { 'data-id': id } : {})}>
                {children}
            </span>
        )
    }

    return createElement(tag, null, ...children)
}

/** React-вузли повідомлення з HTML — лише теги й атрибути з білого списку. */
export function renderRichMessage(html: string): ReactNode {
    if (!html) return null

    const doc = new DOMParser().parseFromString(html, 'text/html')

    return <>{convertChildren(doc.body, 0)}</>
}
